import {
  Button,
  Callout,
  Dialog,
  DialogActions,
  DialogBody,
  DialogError,
  DialogTitle,
  Form,
  SchemaDrivenFieldset,
  Spinner,
  Text
} from '@criteria-labs/react'
import { fileOpen } from 'browser-fs-access'
import { useState } from 'react'
import { ProjectFile } from '../../project/ProjectItem'
import { ProjectManager } from '../../project/ProjectManager'
import { useProjectSelection } from '../../project/selection/useProjectSelection'

export function ImportOpenAPIDialog({ open, onClose }: { open: boolean; onClose: (open: boolean) => void }) {
  const { selection, select } = useProjectSelection()
  const [isSubmitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const handleSubmit = async ({ url }: { url: string }) => {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`The URL returned a ${response.status} status code`)
    }
    if (!response.body) {
      throw new Error('The URL did not contain any data')
    }

    const text = await response.text()
    const urlObject = new URL(url)
    const segments = urlObject.pathname.split('/')
    const name = segments.length > 0 ? segments[segments.length - 1] : ''

    const parentFolder = ProjectManager.shared().getNearestFolder(selection)
    ProjectManager.shared().createFileSync(parentFolder, name, text, (createdFile: ProjectFile) => {
      select(createdFile.path, true)
    })
  }

  const handleImportFile = async () => {
    try {
      const file = await fileOpen({
        mimeTypes: ['text/*', 'application/json', 'application/yaml']
      })
      const text = await file.text()
      const name = file.name

      const parentFolder = ProjectManager.shared().getNearestFolder(selection)
      ProjectManager.shared().createFileSync(parentFolder, name, text, (createdFile: ProjectFile) => {
        select(createdFile.path, true)
      })
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'AbortError') {
          return
        }
      }
      throw error
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <Form
        defaultValues={{ url: '' }}
        onSubmit={async (values) => {
          try {
            setSubmitting(true)
            await handleSubmit(values)
            onClose(false)
          } catch (error) {
            setError(error instanceof Error ? error : new Error(error ? String(error) : 'An error occured.'))
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <DialogTitle>Import an OpenAPI File</DialogTitle>
        {error && (
          <DialogError>
            <Callout color="red">{error.message}</Callout>
          </DialogError>
        )}
        <DialogBody>
          <SchemaDrivenFieldset
            schema={{
              type: 'object',
              required: ['url'],
              properties: {
                url: { title: 'Enter a URL to import an OpenAPI file from the web', type: 'string' }
              }
            }}
          />

          <Text className="mt-6">
            Or{' '}
            <button
              className="text-zinc-950 underline decoration-zinc-950/50 hover:decoration-zinc-950 dark:text-white dark:decoration-white/50 dark:hover:decoration-white"
              onClick={() => {
                onClose(false)
                handleImportFile()
              }}
            >
              upload a file
            </button>{' '}
            from your computer.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} color="indigo">
            {isSubmitting && <Spinner className="h-[1rem] w-[1rem]" />}
            {isSubmitting && <>Importing&hellip;</>}
            {!isSubmitting && 'Import'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
